import { array, ord, string } from 'fp-ts';
import { createSelector } from 'reselect';
import { flow, pipe } from 'fp-ts/function';
import { ApplicationState, isCustomerAccountNote } from '@mews-commander/types';
import { createAction, createAsyncActionTypes } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import {
    AccountNote,
    AccountNoteCompanyClassifications,
    AccountNoteCustomerClassifications,
} from '@mews-commander/data-access';
import { localizeText, localizeEnum } from '@mews-commander/utils';
import prefix from '../prefix';
import parentSelector from '../selector';
import { createBaseEntitySelectors, createEntitiesReducer, createEntitySelectorByAccountId } from './utils';
import { getCustomerNotes } from './customers';

export const NAME = 'accountNotes';

export const setAccountNotes = (entities: Array<AccountNote>) => createAction(ACCOUNT_NOTES_LOADED, { entities });

export const [getAccountNotes, getAccountNotesByIds, getAccountNoteByOwnerId, getAccountNote] =
    createBaseEntitySelectors<ApplicationState, AccountNote, typeof NAME>(parentSelector, NAME);

export const ACCOUNT_NOTES_LOADED = prefix('ACCOUNT_NOTES_LOADED');
export const ACCOUNT_NOTE_ADD = prefix('ACCOUNT_NOTE_ADD');
export const [ACCOUNT_NOTE_ADD_STARTED, ACCOUNT_NOTE_ADD_SUCCEEDED, ACCOUNT_NOTE_ADD_FAILED] =
    createAsyncActionTypes(ACCOUNT_NOTE_ADD);
export const ACCOUNT_NOTE_UPDATE = prefix('ACCOUNT_NOTE_UPDATE');
export const [ACCOUNT_NOTE_UPDATE_STARTED, ACCOUNT_NOTE_UPDATE_SUCCEEDED, ACCOUNT_NOTE_UPDATE_FAILED] =
    createAsyncActionTypes(ACCOUNT_NOTE_UPDATE);
export const ACCOUNT_NOTE_DELETE = prefix('ACCOUNT_NOTE_DELETE');
export const [ACCOUNT_NOTE_DELETE_STARTED, ACCOUNT_NOTE_DELETE_SUCCEEDED, ACCOUNT_NOTE_DELETE_FAILED] =
    createAsyncActionTypes(ACCOUNT_NOTE_DELETE);

export default createEntitiesReducer({
    FETCH_SUCCESS: ACCOUNT_NOTES_LOADED,
    ADD_SUCCESS: ACCOUNT_NOTE_ADD_SUCCEEDED,
    UPDATE_SUCCESS: ACCOUNT_NOTE_UPDATE_SUCCEEDED,
    DELETE_SUCCESS: ACCOUNT_NOTE_DELETE_SUCCEEDED,
});

export const getAccountNotesByAccountId = createEntitySelectorByAccountId(getAccountNotes);

export const getClassificationsPrefix = (note: AccountNote): keyof Mews.EnumLocalization['prefixes'] =>
    isCustomerAccountNote(note) ? 'AccountNoteCustomerClassifications' : 'AccountNoteCompanyClassifications';

const createCategorizedNoteString = (note: AccountNote | string) => {
    if (typeof note === 'string') {
        const localizedCustomerNotes = localizeText(TextKey.AccountNoteCustomerClassificationsCustomerNotes);
        return `[${localizedCustomerNotes}]${UnicodeEntity.NoBreakSpace}${note}`;
    }

    const { classifications } = note.data.value;
    const classificationPrefix = getClassificationsPrefix(note);

    return pipe(
        classifications,
        array.map((classification: AccountNoteCustomerClassifications | AccountNoteCompanyClassifications) =>
            localizeEnum(classificationPrefix, classification)
        ),
        (classArr) => classArr.join(' ,'),
        (localizedClassificationsString) =>
            `[${localizedClassificationsString}]${UnicodeEntity.NoBreakSpace}${note.content}`
    );
};

export const createConcatenatedNotesString = (notes: Array<AccountNote | string>) => {
    if (notes.length === 0) return null;

    return pipe(
        notes,
        array.map((note) => createCategorizedNoteString(note)),
        (arr) => arr.join(' ')
    );
};

const byClassification: ord.Ord<AccountNote> = pipe(
    string.Ord,
    ord.contramap((note) => localizeEnum(getClassificationsPrefix(note), note.data.value.classifications[0]))
);

const getAccountNotesSortedByCategory = flow(getAccountNotesByAccountId, array.sort(byClassification));

export const getCustomerRelatedNotesString = createSelector(
    getAccountNotesSortedByCategory,
    getCustomerNotes,
    (accountNotes, customerNotes) => {
        const notes = customerNotes ? [...accountNotes, customerNotes] : accountNotes;
        return createConcatenatedNotesString(notes);
    }
);
